import { z } from 'zod';
/**
 * What an export asks for and what comes back.
 *
 * Split out of contract.ts, which carried the whole API contract in one file.
 * Nothing here changed on the way out.
 */
// ---------- Export -------------------------------------------------------------
/**
 * The first eight columns are frozen: this is the shape the marketing team's
 * downstream process already consumes. Everything after is additive.
 */
export const LEGACY_COLUMNS = [
    'Account Name',
    'Website',
    'Person Name',
    'Title',
    'LinkedIn URL',
    'Email',
    'Phone',
    'Status',
];
/**
 * The compatible profile: the eight frozen columns, then Email Check - whether
 * the address in Email was verified against the company's domain, and if not,
 * why not. Email carries every address the portal shows; this column is how a
 * mail merge keeps to the verified ones. Appended, so every reader of the eight
 * still finds them where they were.
 */
export const COMPATIBLE_COLUMNS = [...LEGACY_COLUMNS, 'Email Check'];
export const EXTENDED_COLUMNS = [
    'Row Status Detail',
    'Email Confidence',
    'Mail Merge Safe',
    'Company Match',
    'Seniority',
    'Confidence',
    'Resolved Domain',
    'Company LinkedIn URL',
    'Found By',
    'First Name',
    'Last Name',
    'City',
    'Country',
    'Outreach Status',
    'Notes',
    'Credits Used',
    // Appended, never inserted. Somebody's sheet reads these by position.
    'First Seen',
    'Last Updated',
    // Whether the address in Email was verified against the company's domain, in
    // words. The same column the compatible profile ends with.
    'Email Check',
];
/** How many contacts one export may carry. Exported so the UI cannot drift a second copy of it. */
export const EXPORT_MAX_CONTACTS = 5000;
/**
 * How many people one export by filters may carry.
 *
 * Set from the table, not copied from the selection cap above. On 2026-09-23
 * Quarry held 10,280 contacts, 8,266 of them accepted, and accepted is what the
 * Contacts screen shows before anybody touches a filter. The 5,000 a ticked
 * selection may carry would cut that default view roughly in half, and which
 * half survived would depend only on the sort order.
 *
 * About twice the whole table, so everything fits today and the cap is reached
 * only once the store has doubled. What it protects is one worker run: the build
 * joins each person to their latest employment 25 at a time, inside a Lambda
 * that stops after five minutes.
 */
export const EXPORT_MAX_MATCHING = 20_000;
/**
 * A filter value the list takes as written. Never empty, because the list reads
 * an empty value as no filter at all. No length limit, because the list has none.
 */
function filterValue() {
    return z.string().min(1);
}
/**
 * The Contacts screen's filters, used to choose who goes in an export.
 *
 * These are the names GET /api/contacts reads from its query string, because an
 * export by filters IS that list, read to the end rather than a page at a time.
 *
 * Strict, and closed wherever the list's reader would otherwise shrug. The list
 * ignores a key or a value it does not recognise, which on a screen shows a few
 * extra people. In a file the same thing adds people nobody sees, because an
 * ignored filter widens the export. So an unknown key is refused, and so is any
 * value the reader would ignore, an empty one included. Values it compares for
 * equality stay open: a wrong one matches nobody, and that can only narrow the
 * export.
 *
 * Never stricter than the list, though. A view the screen can show has to be
 * exportable, so a value the list reads is accepted here too.
 */
export const ExportFiltersSchema = z
    .object({
    q: filterValue().optional(),
    company: filterValue().optional(),
    seniority: filterValue().optional(),
    gate: filterValue().optional(),
    outreach: filterValue().optional(),
    // `yes` and `no` are how links and saved views made before 2026-09-06 spell
    // `found` and `missing`. The list still reads them, so an export must too.
    workEmail: z.enum(['found', 'none', 'not_found', 'pending', 'failed', 'missing', 'yes', 'no']).optional(),
    grade: filterValue().optional(),
    country: filterValue().optional(),
    phone: z.enum(['found', 'none', 'not_found', 'requested', 'pending', 'failed', 'missing']).optional(),
    crm: z.enum(['zoho', 'none']).optional(),
    sort: z.enum(['added', 'updated']).optional(),
    dir: z.enum(['asc', 'desc']).optional(),
})
    .strict();
/**
 * An export is scoped one of three ways, and they mean different things.
 *
 * A JOB export is an audit of an uploaded list: every input company yields at
 * least one row, in input order, and a company with no contacts becomes a
 * placeholder carrying the reason.
 *
 * A CONTACTS export is a hand-picked set of people. There is no input list to
 * audit, so there are no placeholders: the sheet contains exactly the people
 * that were selected and nothing else.
 *
 * A FILTERS export is the Contacts list read to the end: everyone its filters
 * match, in the order it shows them, up to {@link EXPORT_MAX_MATCHING}. The
 * people are found by the worker, not listed in the request. Ticking them page
 * by page stopped at a hundred, the largest page the grid shows.
 */
export const ExportRequestSchema = z
    .object({
    jobId: z.string().min(1).optional(),
    linkedinUrls: z.array(z.string().min(1)).min(1).max(EXPORT_MAX_CONTACTS).optional(),
    filters: ExportFiltersSchema.optional(),
    format: z.enum(['xlsx', 'csv']).default('xlsx'),
    profile: z.enum(['compatible', 'full']).default('full'),
    onlyWithEmail: z.boolean().default(false),
    excludeDoNotContact: z.boolean().default(true),
})
    .refine((v) => scopesNamed(v) === 1, {
    message: 'Give exactly one of a jobId, a list of contacts or a set of filters',
});
/** How many of the three scopes a request names. */
function scopesNamed(v) {
    return [Boolean(v.jobId), Boolean(v.linkedinUrls?.length), v.filters !== undefined].filter(Boolean).length;
}
/**
 * How many companies one job may carry.
 *
 * A thousand, by choice rather than by capability. A 6,556-row list arrived and
 * the ceiling was briefly raised to 7,000 to admit it; the list is now run in
 * batches of a thousand instead, which keeps each job inside one credit cap, one
 * export and one reviewable queue rather than producing a single run nobody can
 * check.
 *
 * It is a SHARED constant rather than a literal in the upload check, because
 * three readers bound their paging by it. A validation that admits more rows
 * than the readers will page through does not raise the limit, it silently
 * truncates the job — which is what a bound of 5,000 would have done the moment
 * the ceiling passed it.
 */
/**
 * The statuses a company row never leaves on its own.
 *
 * One list, because two copies of it disagreed and the disagreement was
 * expensive. The company worker skipped a redelivered message only for `done`,
 * `failed` and `cancelled`, so a row that ended `no_people_found`,
 * `dead_domain` or `company_not_found` was re-run from the top: the ladder
 * walked again, credits were spent again, and the job counters were incremented
 * again. Measured on a real 1,000-company job, that came to 473 re-runs — the
 * job reported 1,469 of 1,000 processed, with `succeeded` exactly right because
 * `done` was the one status the list did contain.
 */
export const TERMINAL_COMPANY_STATUSES = [
    'done',
    'failed',
    'cancelled',
    'no_people_found',
    'company_not_found',
    'dead_domain',
];
export function isTerminalCompanyStatus(status) {
    return TERMINAL_COMPANY_STATUSES.includes(status ?? '');
}
export const MAX_COMPANIES_PER_JOB = 1000;
/** The schema default, exported so the UI cannot drift a second copy of it. */
export const DEFAULT_CREDIT_CAP_TOTAL = 20000;
//# sourceMappingURL=exports.js.map